import { useEffect, useState } from 'react'

// Global "since <border>" time window that drives the sidebar's activity
// counters (email-issues added, new matters, attachments added). ONE window
// at a time, user-toggleable, applied to every counter at once — it replaces
// the old per-folder "since you last opened it" delta. Stored per-workstation
// in localStorage (same pattern as me_product_name / me_timeline_style).
//
// Relative presets (2h / 24h / 7d …) are sent to the backend as a RELATIVE
// amount (?hours= / ?days=), so the server resolves "now" fresh on every poll
// and the window stays live WITHOUT the react-query key drifting each render.
// 'custom' is an absolute datetime the user pins ("since 12:00 today").

export type WindowPreset = '2h' | '12h' | '24h' | '7d' | '30d' | '90d' | 'all' | 'custom' | 'rel'
// `at`   = ISO datetime for 'custom' (a pinned absolute border).
// `hours` = continuous "last N hours" set by the sidebar dial (preset 'rel').
//           The backend /counters endpoint takes float hours directly, so the
//           dial can land on any value — no fixed presets. (Ken 2026-08-17.)
export type CounterWindow = { preset: WindowPreset; at?: string; hours?: number }

const KEY = 'me_counter_window'
const EVT = 'me-counter-window-change'
const PRESETS: WindowPreset[] = ['2h', '12h', '24h', '7d', '30d', '90d', 'all', 'custom', 'rel']
// Default 24h — the "newness border" for the sidebar activity counters
// (Ken 2026-08-11).
const DEFAULT: CounterWindow = { preset: '24h' }

export function loadCounterWindow(): CounterWindow {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '{}')
    let preset = raw.preset
    // Migrate the old day-only scheme ('30' | '90' | '180' | '365') → '<n>d'.
    if (typeof preset === 'string' && /^\d+$/.test(preset)) {
      preset = ['7', '30', '90'].includes(preset) ? `${preset}d` : '30d'
    }
    if (!PRESETS.includes(preset)) preset = DEFAULT.preset
    const at = typeof raw.at === 'string' ? raw.at : (typeof raw.date === 'string' ? raw.date : undefined)
    const hours = typeof raw.hours === 'number' && raw.hours > 0 ? raw.hours : undefined
    return { preset, at, hours }
  } catch {
    return DEFAULT
  }
}

export function saveCounterWindow(w: CounterWindow): void {
  try { localStorage.setItem(KEY, JSON.stringify(w)) } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(EVT)) // live update within this tab
}

// `enabled` (default true) lets a caller opt OUT of the live subscription when
// it doesn't need to react to window changes — e.g. MatterTimelinePane only
// tracks the window for the DEMO playhead, and must NOT re-render on every dial
// drag for real tenants (its timeline children aren't memoized). Disabled ⇒ no
// listeners attached; the returned value is the initial load and never updates.
export function useCounterWindow(enabled = true): CounterWindow {
  const [w, setW] = useState<CounterWindow>(loadCounterWindow)
  useEffect(() => {
    if (!enabled) return
    const reload = () => setW(loadCounterWindow())
    window.addEventListener(EVT, reload)
    window.addEventListener('storage', reload)
    return () => {
      window.removeEventListener(EVT, reload)
      window.removeEventListener('storage', reload)
    }
  }, [enabled])
  return w
}

// Query params for /matters/counters/. Relative for presets (backend resolves
// "now"); absolute `since` for 'all' and 'custom'.
export function windowRequestParams(w: CounterWindow): Record<string, string | number> {
  if (w.preset === 'all') return { since: '2000-01-01' }
  if (w.preset === 'rel') return { hours: w.hours && w.hours > 0 ? w.hours : 24 }
  if (w.preset === 'custom') return { since: w.at || '2000-01-01' }
  const n = Number(w.preset.slice(0, -1)) || 24
  return w.preset.endsWith('h') ? { hours: n } : { days: n }
}

// Stable react-query key part — must NOT embed a rolling "now" (that's what
// keeps hour-windows from refetching-thrashing every render). For 'rel' the
// key is the (rounded) hours value, so the counts refetch only when the dial
// actually lands on a new value, not on every pixel of the drag.
export function windowKey(w: CounterWindow): string {
  if (w.preset === 'custom') return `since:${w.at || ''}`
  if (w.preset === 'rel') return `rel:${w.hours || 24}`
  return w.preset
}

// Clean-magnitude rounding so a continuous dial always yields a tidy label
// (10m / 5h / 2d / 3w / 2mo) — returns hours. 5-min steps under an hour.
export function roundHours(h: number): number {
  const min = h * 60
  if (min < 60) return Math.max(5, Math.round(min / 5) * 5) / 60
  if (h < 48) return Math.round(h)
  const d = h / 24
  if (d < 14) return Math.round(d) * 24
  if (d < 60) return Math.round(d / 7) * 24 * 7
  return Math.round(d / 30) * 24 * 30
}

// Compact label for a continuous hours value.
export function relLabel(h: number): string {
  const min = h * 60
  if (min < 60) return `${Math.max(5, Math.round(min / 5) * 5)}m`
  if (h < 48) return `${Math.round(h)}h`
  const d = h / 24
  if (d < 14) return `${Math.round(d)}d`
  if (d < 60) return `${Math.round(d / 7)}w`
  return `${Math.round(d / 30)}mo`
}

// Current window → equivalent "hours back", or null for all-time. Lets the
// dial position its thumb no matter which representation is stored.
export function windowToHours(w: CounterWindow): number | null {
  if (w.preset === 'all') return null
  if (w.preset === 'rel') return w.hours && w.hours > 0 ? w.hours : 24
  if (w.preset === 'custom') {
    if (!w.at) return null
    const ms = Date.now() - new Date(w.at).getTime()
    return ms > 0 ? ms / 3.6e6 : 1
  }
  const n = Number(w.preset.slice(0, -1)) || 24
  return w.preset.endsWith('h') ? n : n * 24
}

// Build a window from a dialled hours value (null = all time).
export function windowFromHours(h: number | null): CounterWindow {
  return h === null ? { preset: 'all' } : { preset: 'rel', hours: roundHours(h) }
}

export function windowLabel(w: CounterWindow): string {
  if (w.preset === 'all') return 'All time'
  if (w.preset === 'rel') return `Last ${relLabel(w.hours || 24)}`
  if (w.preset === 'custom') {
    if (!w.at) return 'Custom…'
    const d = new Date(w.at)
    if (isNaN(d.getTime())) return `Since ${w.at}`
    const hasTime = w.at.includes('T')
    return 'Since ' + d.toLocaleString([], hasTime
      ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric' })
  }
  return `Last ${w.preset}`
}
